import TransactionsController from './TransactionsController';
import { apiRequest } from '../utils/useAPIRequest';
import constant from '../utils/constant';
import { businessStore, invoiceStore } from '../stores';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from '../App';
import { useMemo } from 'react';
import { refreshQuery } from '../utils/utils';

const getAnalysis = async query => {
	if (!query) {
		const startDate = moment().startOf('year');
		const endDate = moment().endOf('month');
		query = `startDate=${startDate}&endDate=${endDate}&dateField=createdAt`;
	}
	const { status, data, errorMessage } = await apiRequest({
		url: `/invoices/new/analysis?${query}`,
		method: 'get',
	});

	if (status === constant.Success) {
		invoiceStore.setInvoicesAnalysis(data);
		// invoiceStore.setSelectedCurrency(data.statuses.length === 0 ? "NGN" : data.statuses[0]._id);
		invoiceStore.setSelectedCurrency(
			businessStore.defaultCurrency
				? businessStore.defaultCurrency
				: data.statuses[0]._id
		);

		return { status: constant.Success };
	}
	return { errorMessage, status };
};

const getInvoiceConfig = async () => {
	const { status, data, errorMessage } = await apiRequest({
		url: '/settings/business',
		method: 'get',
	});

	if (status === constant.Success) {
		invoiceStore.setInvoiceConfig(data);

		return { status: constant.Success };
	}

	return { errorMessage, status };
};

const updateInvoiceConfig = async payload => {
	const { status, errorMessage } = await apiRequest({
		url: '/settings/update-setting',
		method: 'patch',
		payload,
	});
	if (status === constant.Success) {
		await getInvoiceConfig();
		return { status: constant.Success };
	}
	return { errorMessage, status };
};

const useInvoiceList = (invoiceTableQuery, filterQuery, searchQuery) => {
	const id = businessStore.bId;

	const query = useMemo(() => {
		if (searchQuery) {
			return `${invoiceTableQuery}&${filterQuery}&search=${searchQuery}`;
		} else if (filterQuery) {
			return `${invoiceTableQuery}&${filterQuery}`;
		} else {
			return invoiceTableQuery;
		}
	}, [filterQuery, invoiceTableQuery, searchQuery]);

	return useQuery({
		queryKey: [id, 'invoicelist', query],
		queryFn: () => {
			return apiRequest({
				url: `/invoices/new/business?${query}`,
				method: 'get',
			});
		},
		keepPreviousData: true,
	});
};

const sendInvoice = async id =>
	await apiRequest({
		url: `/invoices/invoice/send/${id}`,
		method: 'get',
	});

const download = async id =>
	await apiRequest({
		url: `/invoices/download-invoice/${id}`,
		method: 'get',
		hasImageDownload: true,
	});

const updateInvoice = async (payload, id) => {
	const { status, data, errorMessage } = await apiRequest({
		url: `/invoices/invoice/${id}`,
		method: 'put',
		payload,
	});
	if (status === constant.Success) {
		invoiceStore.setSelectedInvoice(data);
		const id = businessStore.bId;
		refreshQuery({
			queryKey: [constant.apiQueryKey.Transactions.TransactionList],
		});
		queryClient.invalidateQueries({ queryKey: [id, 'invoicelist'] });
		Promise.all([getAnalysis(), TransactionsController.transactionSummary()]);
		return { status: constant.Success };
	}
	return { errorMessage, status };
};

const updateInvoiceStatus = async payload => {
	const id = businessStore.bId;

	const { status, data, errorMessage } = await apiRequest({
		url: '/invoices/invoice/status',
		method: 'patch',
		payload,
	});
	if (status === constant.Success) {
		invoiceStore.setSelectedInvoice(data);
		refreshQuery({
			queryKey: [constant.apiQueryKey.Transactions.TransactionList],
		});
		queryClient.invalidateQueries({ queryKey: [id, 'invoicelist'] });
		await Promise.all([
			getAnalysis(),
			TransactionsController.transactionSummary(),
		]);
		return { status: constant.Success };
	}
	return { errorMessage, status };
};

const createInvoice = async payload => {
	const id = businessStore.bId;

	const { status, data, errorMessage } = await apiRequest({
		url: '/invoices/invoice',
		method: 'post',
		payload,
	});
	if (status === constant.Success) {
		queryClient.invalidateQueries({ queryKey: [id, 'invoicelist'] });
		invoiceStore.setSelectedInvoice(data);
		Promise.all([getAnalysis(), TransactionsController.transactionSummary()]);
		return { status: constant.Success, data };
	}
	return { errorMessage, status };
};

const createBulkInvoice = async payload => {
	const id = businessStore.bId;

	const { status, data, errorMessage } = await apiRequest({
		url: '/invoices/invoice/bulk',
		method: 'post',
		payload,
	});
	if (status === constant.Success) {
		invoiceStore.setSelectedInvoice(data);
		queryClient.invalidateQueries({ queryKey: [id, 'invoicelist'] });
		refreshQuery({
			queryKey: [constant.apiQueryKey.Transactions.TransactionList],
		});
		await Promise.all([
			getAnalysis(),
			TransactionsController.transactionSummary(),
		]);
		return { status: constant.Success };
	}
	return { errorMessage, status };
};

const createToshoInvoice = async payload => {
	const { status, data, errorMessage } = await apiRequest({
		url: '/invoices/tosho-invoice',
		method: 'post',
		payload,
	});
	if (status === constant.Success) {
		invoiceStore.setSelectedInvoice(data);

		return { status: constant.Success };
	}
	return { errorMessage, status };
};

const createStockInvoice = async payload => {
	const { status, data, errorMessage } = await apiRequest({
		url: '/invoices/stock-invoice',
		method: 'post',
		payload,
	});
	if (status === constant.Success) {
		invoiceStore.setSelectedInvoice(data);

		return { status: constant.Success };
	}
	return { errorMessage, status };
};

const invoicePaymentCheckout = async id => {
	const { status, data, errorMessage } = await apiRequest({
		url: `/invoices/payment-gateway/${id}`,
		method: 'get',
	});

	if (status === constant.Success) {
		invoiceStore.setCheckoutData(data);
		return { status: constant.Success };
	}

	return { errorMessage, status };
};

const fxTransferCheckout = async (invoiceId, currencyCode) => {
	const { status, data, errorMessage } = await apiRequest({
		url: `/invoices/currency-payment-details/${invoiceId}/${currencyCode}`,
		method: 'get',
	});

	if (status === constant.Success) {
		invoiceStore.setFxTransferCheckoutData(data);
		return { status: constant.Success };
	}

	return { errorMessage, status };
};

const invoicePaymentConfirm = async id => {
	const { status, data, errorMessage } = await apiRequest({
		url: `/invoices/confirm-payment/${id}`,
		method: 'get',
	});

	if (status === constant.Success) {
		return { status: constant.Success, data };
	}
	return { errorMessage, status };
};

const InvoiceController = {
	updateInvoiceConfig,
	getInvoiceConfig,
	getAnalysis,
	download,
	createInvoice,
	createBulkInvoice,
	sendInvoice,
	updateInvoice,
	updateInvoiceStatus,
	createToshoInvoice,
	createStockInvoice,
	invoicePaymentCheckout,
	invoicePaymentConfirm,
	useInvoiceList,
	fxTransferCheckout,
};

export default InvoiceController;
